'use client'

import { i18n } from './i18n'
import { Post } from '@/payload-types'
import { Locale } from '@/providers/Locale/types'
import { formatRelativeTime } from '@/utilities/formatDateTime'
import { cn } from '@/utilities/ui'
import { useCallback, useEffect, useState } from 'react'

interface PublishInfo {
    isPublishedAndUpdatedWithinThreshold: boolean
    isPublishedToday: boolean
    formattedPublishDate: string
    formattedUpdateDate: string
    mostRecentDate: 'publishedAt' | 'updatedAt'
}

interface Props {
    publishedAt: Post['publishedAt']
    updatedAt?: Post['updatedAt']
    locale: Locale
    className?: string
    showDisplayText?: boolean
}

export const PublishedDateInfo: React.FC<Props> = ({ publishedAt, updatedAt, locale, className, showDisplayText = true }) => {
    const [publishInfo, setPublishInfo] = useState<PublishInfo>({
        isPublishedAndUpdatedWithinThreshold: false,
        isPublishedToday: false,
        formattedPublishDate: '',
        formattedUpdateDate: '',
        mostRecentDate: 'publishedAt',
    })

    const showBothPublishedAndUpdatedTime = publishInfo.isPublishedToday && showDisplayText

    // Show published at date if it is provided, and it is either the most recent date, or it is published today
    const showPublishedAtDate = publishInfo.formattedPublishDate && (publishInfo.mostRecentDate === 'publishedAt' || showBothPublishedAndUpdatedTime)

    // Show updated at date if it is provided and the published and updated dates are sufficiently different, and it is either the most recent date or is published today
    const showUpdatedAtDate =
        publishInfo.formattedUpdateDate &&
        !publishInfo.isPublishedAndUpdatedWithinThreshold &&
        (publishInfo.mostRecentDate === 'updatedAt' || showBothPublishedAndUpdatedTime)

    const getPublishInfo = useCallback(() => {
        const publishedDate = publishedAt ? new Date(publishedAt) : null
        const updatedDate = updatedAt ? new Date(updatedAt) : publishedDate
        const fiveMinutesInMs = 1000 * 60 * 5

        return {
            isPublishedAndUpdatedWithinThreshold:
                !!updatedDate && !!publishedDate && Math.abs(updatedDate.getTime() - publishedDate.getTime()) <= fiveMinutesInMs,
            isPublishedToday: publishedDate?.toDateString() === new Date().toDateString(),
            // When a new article is saved, there is a race condition where it can be up to a minute for an updated timestamp to generate
            // Therefore we will use the smallest interval of 5 minutes
            mostRecentDate:
                Math.floor((updatedDate?.getTime() || 0) / fiveMinutesInMs) > Math.floor((publishedDate?.getTime() || 0) / fiveMinutesInMs)
                    ? 'updatedAt'
                    : 'publishedAt',
            formattedPublishDate: formatRelativeTime(publishedAt, locale),
            formattedUpdateDate: formatRelativeTime(updatedAt, locale),
        } satisfies PublishInfo
    }, [updatedAt, publishedAt, locale])

    useEffect(() => {
        // Run the getPublishInfo on client since it uses date.now to prevent rehydration issues
        setPublishInfo(getPublishInfo())
    }, [getPublishInfo, setPublishInfo])

    // If both items are null/undefined, return empty
    if (!publishInfo.formattedPublishDate && !publishInfo.formattedUpdateDate) {
        return null
    }

    return (
        <div className={cn('flex text-base leading-[21px]', className)}>
            {showPublishedAtDate && (
                <time className='truncate' aria-label={i18n[locale].published}>
                    {showDisplayText && i18n[locale].published} {publishInfo.formattedPublishDate}
                </time>
            )}
            {showPublishedAtDate && showUpdatedAtDate && <span>|</span>}
            {showUpdatedAtDate && (
                <time className='truncate' aria-label={i18n[locale].updated}>
                    {showDisplayText && i18n[locale].updated} {publishInfo.formattedUpdateDate}
                </time>
            )}
        </div>
    )
}
